// Process definitions + economic constants.
//
// Each process is tagged with `industries`, `sizes`, and `functions` arrays so
// the picker can filter the catalog to a visitor's context. Use the slug "all"
// in any of those arrays to mean "applies across the dimension."

export const HOURLY_RATE = 150;     // loaded labor cost ($/hr)
export const PROCS_PER_YEAR = 50;   // for "annualized" stats

// ---------- Types ----------

export interface DimensionOption {
  id: string;
  label: string;
}

export interface ProcessOrg {
  id: string;
  name: string;
  color: string;
}

export interface ProcessParty {
  id: string;
  org: string;
  name: string;
}

export interface ProcessStep {
  from: string;
  to: string;
  label: string;
  baseHrs: number;
  workHrs: number;
  aiEligible: boolean;
  /** Optional explicit AI human-review fraction (0..1). Catalog override. */
  aiReviewPct?: number;
}

export interface ProcessDef {
  id: string;
  name: string;
  icon: string;
  category?: string;
  short?: string;
  desc?: string;
  pills?: string[];
  industries?: string[];
  sizes?: string[];
  functions?: string[];
  orgs: ProcessOrg[];
  parties: ProcessParty[];
  steps: ProcessStep[];
  /** Optional override of the noun used in "X / yr saved from failure". */
  outcomeNoun?: string;
  /** Optional override of the "Planapus is doing X" callouts. */
  aiActions?: string[];
  /** Marker set by claude.ts when the process was generated, not from catalog. */
  isCustom?: boolean;
  /** Cached baseline used by simulation.ts. Filled lazily. */
  _baselineMeanHrs?: number;
}

export interface Intake {
  industry: string;
  size: string;
  function: string;
}

export type ProcessCatalog = Record<string, ProcessDef>;

// ---------- Filter dimensions (UI options) ----------

// The "other" option in each dimension lets the visitor describe a
// context that doesn't fit the canned list (e.g. a vertical we don't
// catalog yet). Selecting it is a no-op for the catalog filter — the
// picker shows every process — and surfaces a free-text input alongside
// the chip so the visitor's actual context gets captured. The text is
// stored in PilotContext (and sent with any subsequent lead) so the
// founder follow-up sees what they typed.
export const INDUSTRIES: DimensionOption[] = [
  { id: "all",                 label: "All / Cross-industry" },
  { id: "financial-services",  label: "Financial Services" },
  { id: "saas",                label: "SaaS / Technology" },
  { id: "healthcare",          label: "Healthcare" },
  { id: "manufacturing",       label: "Manufacturing" },
  { id: "retail",              label: "Retail / E-commerce" },
  { id: "professional",        label: "Professional Services" },
  { id: "media",               label: "Media / Entertainment" },
  { id: "energy",              label: "Energy / Utilities" },
  { id: "government",          label: "Government / Public Sector" },
  { id: "other",               label: "Other (describe)" },
];

export const SIZES: DimensionOption[] = [
  { id: "startup",    label: "<50 employees (Startup)" },
  { id: "smb",        label: "50–500 (SMB)" },
  { id: "mid",        label: "500–5,000 (Mid-market)" },
  { id: "enterprise", label: "5,000+ (Enterprise)" },
  { id: "other",      label: "Other (describe)" },
];

export const FUNCTIONS: DimensionOption[] = [
  { id: "operations",        label: "Operations / Ops Excellence" },
  { id: "finance",           label: "Finance / Accounting" },
  { id: "sales",             label: "Sales / Revenue" },
  { id: "hr",                label: "HR / People" },
  { id: "legal",             label: "Legal / Compliance" },
  { id: "engineering",       label: "Engineering / Product" },
  { id: "marketing",         label: "Marketing" },
  { id: "customer-success",  label: "Customer Success" },
  { id: "it",                label: "IT / Security" },
  { id: "procurement",       label: "Procurement / Supply" },
  { id: "other",             label: "Other (describe)" },
];

// ---------- Filter ----------

export function filterProcesses(
  processes: ProcessCatalog,
  intake: Partial<Intake> | null | undefined,
): ProcessDef[] {
  const all = Object.values(processes);
  if (!intake) return all;
  // "other" is the visitor saying "my context isn't in the list" — treat
  // it as no filter for that dimension so they still see every process
  // and can pick one to step through. The freeform description they
  // typed travels with any subsequent lead.
  const wantI = intake.industry === "other" ? "" : intake.industry;
  const wantS = intake.size === "other" ? "" : intake.size;
  const wantF = intake.function === "other" ? "" : intake.function;
  if (!wantI && !wantS && !wantF) return all;
  return all.filter(p => {
    const inds = p.industries || ["all"];
    const sizes = p.sizes || ["all"];
    const fns = p.functions || ["all"];
    const okI = !wantI || inds.includes(wantI) || inds.includes("all");
    const okS = !wantS || sizes.includes(wantS) || sizes.includes("all");
    const okF = !wantF || fns.includes(wantF) || fns.includes("all");
    return okI && okS && okF;
  });
}

// ---------- Portfolio picker ----------
//
// Picks up to `n` distinct processes from the catalog ranked by how well they
// fit the visitor's industry+size context. `currentProc` is always slot 1 so
// the visitor sees the process they just simulated reflected in the rollup.
//
// Ranking is a simple tier model:
//   T1 — direct industry match AND size match
//   T2 — direct industry match (any size, including "all")
//   T3 — cross-industry ("all" in industries) but size matches
//   T4 — any cross-industry catch-all
//   T5 — everything else
//
// Within a tier, order is the catalog order (deterministic = stable copy).

export function pickPortfolio(
  processes: ProcessCatalog,
  currentProc: ProcessDef | null | undefined,
  intake: Partial<Intake> | null | undefined,
  n: number,
): ProcessDef[] {
  const all = Object.values(processes);
  const wantI = (intake && intake.industry) || "";
  const wantS = (intake && intake.size) || "";

  const tier = (p: ProcessDef): number => {
    const inds = p.industries || ["all"];
    const sizes = p.sizes || ["all"];
    const indDirect = !!wantI && inds.includes(wantI);
    const indAny    = inds.includes("all");
    const sizeOk    = !wantS || sizes.includes(wantS);
    if (indDirect && sizeOk) return 1;
    if (indDirect)           return 2;
    if (indAny && sizeOk)    return 3;
    if (indAny)              return 4;
    return 5;
  };

  const ordered = all
    .map((p, i) => ({ p, tier: tier(p), idx: i }))
    .sort((a, b) => (a.tier - b.tier) || (a.idx - b.idx))
    .map(x => x.p);

  const picked: ProcessDef[] = [];
  const seen = new Set<string>();
  if (currentProc) {
    picked.push(currentProc);
    seen.add(currentProc.id);
  }
  for (const p of ordered) {
    if (picked.length >= n) break;
    if (seen.has(p.id)) continue;
    picked.push(p);
    seen.add(p.id);
  }
  return picked;
}

// Pre-baked palette of org colors a custom-generated process can pick from.
export const ORG_PALETTE: string[] = ["#6dd6ff", "#f59f4a", "#b39bff", "#4ade80", "#ff7ab6", "#facc15"];

// Fallback AI-action callouts when a process doesn't define its own. These
// surface on the "With Planapus" lane as concrete examples of what Planapus
// is actually doing — so the AI mode isn't an unexplained miracle.
export const DEFAULT_AI_ACTIONS: string[] = [
  "Drafts the next handoff with full context from prior steps",
  "Auto-follows up at the 4-hour mark when a handoff goes silent",
  "Routes to a fallback approver if the primary is OOO",
  "Reconciles cross-form data so reviewers never re-collect facts already on file",
];

// Builds an AI-actions list tailored to the picked process. Falls back to
// `DEFAULT_AI_ACTIONS` for any catalog process that doesn't define its own,
// then mixes in two derived actions (named participants + named outcome) so
// every list reads as specific to the visitor's choice.
export function tailoredAIActions(proc: ProcessDef | null | undefined): string[] {
  if (!proc) return DEFAULT_AI_ACTIONS;
  const base = Array.isArray(proc.aiActions) && proc.aiActions.length
    ? [...proc.aiActions]
    : [...DEFAULT_AI_ACTIONS];
  // Always end on a process-specific concrete moment, even when the
  // catalog entry already defines its own list — gives readers one beat
  // they can map back to their own world.
  const noun = outcomeNounFor(proc);
  const closing = `Tracks the ${proc.steps.length}-step ${noun} clock end-to-end, with audit + override on every action`;
  if (!base.includes(closing)) base.push(closing);
  return base;
}

// Map a process id to the noun used in "X / yr saved from failure". Falls
// back to a generic "runs" when the process isn't in the map.
const OUTCOME_NOUNS: Record<string, string> = {
  "onboarding": "deals",
  "hiring": "hires",
  "rfp": "vendor selections",
  "vendor-onboarding": "vendor activations",
  "sales-quote": "deals",
  "expense-exception": "approvals",
  "software-access": "access requests",
  "loan-origination": "loans funded",
  "marketing-campaign": "campaigns shipped",
  "patient-referral": "referrals",
  "facility-permits": "permits issued",
  "tos-management": "policy updates shipped",
};

export function outcomeNounFor(proc: ProcessDef | null | undefined): string {
  if (!proc) return "runs";
  if (proc.outcomeNoun) return proc.outcomeNoun;
  return OUTCOME_NOUNS[proc.id] || "runs";
}

export function aiActionsFor(proc: ProcessDef | null | undefined): string[] {
  return tailoredAIActions(proc);
}

// ---------- Catalog ----------

export const PROCESSES: ProcessCatalog = {
  // ============================================================
  // Food Manufacturing — Facility Permits (lead wedge)
  // ============================================================
  "facility-permits": {
    id: "facility-permits",
    name: "Food Manufacturing — Facility Permits",
    icon: "🏭",
    category: "certification",
    short: "Build/expansion permit packet across FDA, state ag, county health, and local zoning.",
    desc: "A multi-jurisdiction permit packet for a new or expanded food facility. Plans, equipment lists, sanitary design, and process flows bounce between corporate operations, an external engineer, the FDA, state ag, county health, and local zoning. Each agency reviews on its own clock; one missing signature stalls the build.",
    pills: ["~12 handoffs", "5 orgs", "Typical: 8–16 weeks"],
    industries: ["manufacturing"],
    sizes: ["mid", "enterprise"],
    functions: ["operations", "legal"],
    outcomeNoun: "permits issued",
    aiActions: [
      "Reconciles plan-set + equipment list against FDA + state ag checklists; flags gaps before submission",
      "Pulls the right SOPs + sanitary design specs into each agency's template",
      "Tracks each agency's review clock and pings the right reviewer when a packet goes silent",
      "Routes cross-agency dependencies (e.g. county health waits on state ag sign-off) so nothing sits in an inbox",
    ],
    orgs: [
      { id: "ops",      name: "Facility ops",     color: "#6dd6ff" },
      { id: "eng",      name: "External engineer", color: "#b39bff" },
      { id: "fda",      name: "FDA",              color: "#f59f4a" },
      { id: "stateag",  name: "State ag",         color: "#4ade80" },
      { id: "local",    name: "County / zoning",  color: "#ff7ab6" },
    ],
    parties: [
      { id: "plant",   org: "ops",     name: "Plant Manager" },
      { id: "qaops",   org: "ops",     name: "QA / Compliance" },
      { id: "olegal",  org: "ops",     name: "Operations Counsel" },
      { id: "designer",org: "eng",     name: "Design Engineer" },
      { id: "fdarev",  org: "fda",     name: "FDA Reviewer" },
      { id: "agrev",   org: "stateag", name: "State Ag Inspector" },
      { id: "health",  org: "local",   name: "County Health" },
      { id: "zoning",  org: "local",   name: "Local Zoning" },
    ],
    steps: [
      { from: "plant",   to: "designer", label: "Brief engineer on scope",        baseHrs: 12, workHrs: 2,   aiEligible: true  },
      { from: "designer",to: "qaops",    label: "Return plan set + equipment list", baseHrs: 60, workHrs: 8, aiEligible: false },
      { from: "qaops",   to: "olegal",   label: "Compile FDA submission packet",  baseHrs: 36, workHrs: 6,   aiEligible: true  },
      { from: "olegal",  to: "fdarev",   label: "Submit to FDA (FCE / SID)",      baseHrs: 8,  workHrs: 0.5, aiEligible: true  },
      { from: "fdarev",  to: "qaops",    label: "FDA request for additional info", baseHrs: 80, workHrs: 4,  aiEligible: true  },
      { from: "qaops",   to: "fdarev",   label: "Respond with HACCP + SOPs",      baseHrs: 48, workHrs: 8,   aiEligible: true  },
      { from: "qaops",   to: "agrev",    label: "Submit state ag application",    baseHrs: 18, workHrs: 1,   aiEligible: true  },
      { from: "agrev",   to: "qaops",    label: "State ag plan review feedback",  baseHrs: 72, workHrs: 4,   aiEligible: false },
      { from: "qaops",   to: "health",   label: "Submit county health permit",    baseHrs: 18, workHrs: 1,   aiEligible: true  },
      { from: "health",  to: "plant",    label: "Schedule on-site inspection",    baseHrs: 56, workHrs: 1,   aiEligible: true  },
      { from: "plant",   to: "zoning",   label: "Submit local zoning + occupancy", baseHrs: 24, workHrs: 1,  aiEligible: true  },
      { from: "zoning",  to: "plant",    label: "Final occupancy + operating permit", baseHrs: 60, workHrs: 1, aiEligible: false },
    ],
  },

  // ============================================================
  // Terms of Service Management
  // ============================================================
  "tos-management": {
    id: "tos-management",
    name: "Terms of Service Management",
    icon: "📜",
    category: "ip",
    short: "Policy redraft, legal review, regional review, customer notice, in-app rollout.",
    desc: "Updating the terms of service is more than a redraft. Legal, product, marketing, regional counsel, and customer support all touch the change. Customers need notice, the in-app surface needs the new copy, and the audit trail needs to show who approved what.",
    pills: ["~11 handoffs", "3 orgs", "Typical: 3–6 weeks"],
    industries: ["all", "saas", "financial-services", "retail"],
    sizes: ["mid", "enterprise"],
    functions: ["legal", "operations", "customer-success"],
    outcomeNoun: "policy updates shipped",
    aiActions: [
      "Diffs the proposed redraft against the live ToS and against peer-company policies",
      "Routes regional-counsel review only for clauses that materially changed in that region",
      "Drafts the customer notice + in-app banner copy from the redline",
      "Tracks the 30-day notice clock and triggers the in-app surface to flip on the right day",
    ],
    orgs: [
      { id: "vendor",  name: "Your org",      color: "#6dd6ff" },
      { id: "regional",name: "Regional counsel", color: "#b39bff" },
      { id: "customer",name: "Customers",     color: "#f59f4a" },
    ],
    parties: [
      { id: "tlegal",  org: "vendor",  name: "Legal Lead" },
      { id: "pm",      org: "vendor",  name: "Product Manager" },
      { id: "comms",   org: "vendor",  name: "Marketing / Comms" },
      { id: "cs",      org: "vendor",  name: "Customer Support" },
      { id: "eng",     org: "vendor",  name: "Engineering" },
      { id: "exec",    org: "vendor",  name: "Exec Sponsor" },
      { id: "eulegal", org: "regional",name: "EU Counsel" },
      { id: "uklegal", org: "regional",name: "UK / APAC Counsel" },
      { id: "cust",    org: "customer",name: "Customers" },
    ],
    steps: [
      { from: "tlegal", to: "pm",      label: "Brief product on policy change",     baseHrs: 8,  workHrs: 1,   aiEligible: true  },
      { from: "pm",     to: "tlegal",  label: "Confirm product-facing impact",      baseHrs: 18, workHrs: 1,   aiEligible: true  },
      { from: "tlegal", to: "eulegal", label: "Route EU regional review",           baseHrs: 24, workHrs: 0.5, aiEligible: true  },
      { from: "eulegal",to: "tlegal",  label: "EU redlines (GDPR / DSA)",           baseHrs: 60, workHrs: 4,   aiEligible: false },
      { from: "tlegal", to: "uklegal", label: "Route UK / APAC review",             baseHrs: 24, workHrs: 0.5, aiEligible: true  },
      { from: "uklegal",to: "tlegal",  label: "UK / APAC redlines",                 baseHrs: 56, workHrs: 3,   aiEligible: false },
      { from: "tlegal", to: "comms",   label: "Draft customer notice",              baseHrs: 18, workHrs: 2,   aiEligible: true  },
      { from: "comms",  to: "tlegal",  label: "Marketing review of notice",         baseHrs: 18, workHrs: 1,   aiEligible: true  },
      { from: "tlegal", to: "exec",    label: "Exec sign-off",                      baseHrs: 28, workHrs: 0.5, aiEligible: true  },
      { from: "tlegal", to: "eng",     label: "Hand off final copy to engineering", baseHrs: 12, workHrs: 0.5, aiEligible: true  },
      { from: "eng",    to: "cust",    label: "Ship in-app notice + new ToS",       baseHrs: 24, workHrs: 4,   aiEligible: false },
    ],
  },

  // ============================================================
  // B2B Customer Onboarding & KYC
  // ============================================================
  onboarding: {
    id: "onboarding",
    name: "B2B Customer Onboarding & KYC",
    icon: "🏢",
    category: "financial",
    short: "MSA, security review, KYC, finance approval, kickoff.",
    desc: "From signed term sheet to live customer. Sales hands to legal hands to security hands to finance hands to implementation, across two organizations.",
    pills: ["~13 handoffs", "2 orgs", "Typical: 18–35 days"],
    industries: ["all", "saas", "financial-services", "professional"],
    sizes: ["smb", "mid", "enterprise"],
    functions: ["operations", "sales", "legal", "customer-success"],
    orgs: [
      { id: "vendor",   name: "Vendor (you)",       color: "#6dd6ff" },
      { id: "customer", name: "Customer",           color: "#f59f4a" },
      { id: "external", name: "External providers", color: "#b39bff" },
    ],
    parties: [
      { id: "ae",      org: "vendor",   name: "Account Executive" },
      { id: "vlegal",  org: "vendor",   name: "Vendor Legal" },
      { id: "vse",     org: "vendor",   name: "Solutions Eng / Security" },
      { id: "vops",    org: "vendor",   name: "Compliance / KYC" },
      { id: "vimpl",   org: "vendor",   name: "Implementation / CSM" },
      { id: "buyer",   org: "customer", name: "Buyer / Champion" },
      { id: "clegal",  org: "customer", name: "Customer Legal" },
      { id: "cproc",   org: "customer", name: "Procurement" },
      { id: "csec",    org: "customer", name: "InfoSec Review" },
      { id: "cfin",    org: "customer", name: "Finance / AP" },
      { id: "kycprov", org: "external", name: "KYC Provider" },
    ],
    steps: [
      { from: "ae",     to: "buyer",   label: "Send MSA draft",                 baseHrs: 8,  workHrs: 0.5, aiEligible: true  },
      { from: "buyer",  to: "clegal",  label: "Route MSA to Legal",             baseHrs: 14, workHrs: 0.3, aiEligible: true  },
      { from: "clegal", to: "ae",      label: "Redline MSA",                    baseHrs: 48, workHrs: 4,   aiEligible: false },
      { from: "ae",     to: "vlegal",  label: "Forward redlines",               baseHrs: 8,  workHrs: 0.3, aiEligible: true  },
      { from: "vlegal", to: "clegal",  label: "Counter-redline",                baseHrs: 36, workHrs: 3,   aiEligible: false },
      { from: "clegal", to: "vlegal",  label: "Final MSA agreement",            baseHrs: 24, workHrs: 1.5, aiEligible: false },
      { from: "ae",     to: "cproc",   label: "Request security questionnaire", baseHrs: 8,  workHrs: 0.2, aiEligible: true  },
      { from: "cproc",  to: "vse",     label: "Send 200-question SIG",          baseHrs: 12, workHrs: 0.3, aiEligible: true  },
      { from: "vse",    to: "csec",    label: "Complete & return SIG",          baseHrs: 28, workHrs: 8,   aiEligible: true  },
      { from: "csec",   to: "cfin",    label: "Security sign-off",              baseHrs: 32, workHrs: 4,   aiEligible: false },
      { from: "cfin",   to: "ae",      label: "PO issued",                      baseHrs: 24, workHrs: 1,   aiEligible: true  },
      { from: "vops",   to: "kycprov", label: "Run KYC / sanctions check",      baseHrs: 16, workHrs: 0.5, aiEligible: true  },
      { from: "vimpl",  to: "buyer",   label: "Implementation kickoff",         baseHrs: 8,  workHrs: 1,   aiEligible: true  },
    ],
  },

  // ============================================================
  // Senior Engineer Hiring Loop
  // ============================================================
  hiring: {
    id: "hiring",
    name: "Senior Engineer Hiring Loop",
    icon: "👥",
    category: "employment",
    short: "Sourcing, screen, panel, debrief, offer, negotiation.",
    desc: "Recruiter coordinates a hiring manager, four interviewers, comp, and one candidate's calendar (across multiple time zones) without losing the candidate to a faster competitor.",
    pills: ["~16 handoffs", "Candidate is external", "Typical: 4–8 weeks"],
    industries: ["all"],
    sizes: ["smb", "mid", "enterprise"],
    functions: ["hr", "engineering"],
    orgs: [
      { id: "company",   name: "Hiring company", color: "#6dd6ff" },
      { id: "candidate", name: "Candidate",      color: "#f59f4a" },
    ],
    parties: [
      { id: "rec",  org: "company",   name: "Recruiter" },
      { id: "hm",   org: "company",   name: "Hiring Manager" },
      { id: "i1",   org: "company",   name: "Tech Screen Interviewer" },
      { id: "i2",   org: "company",   name: "Onsite Interviewer #1" },
      { id: "i3",   org: "company",   name: "Onsite Interviewer #2" },
      { id: "i4",   org: "company",   name: "Onsite Interviewer #3" },
      { id: "comp", org: "company",   name: "Compensation / HR" },
      { id: "cand", org: "candidate", name: "Candidate" },
    ],
    steps: [
      { from: "rec",  to: "hm",   label: "Submit shortlist",          baseHrs: 6,  workHrs: 0.3, aiEligible: true  },
      { from: "hm",   to: "rec",  label: "Approve for screen",        baseHrs: 14, workHrs: 0.5, aiEligible: true  },
      { from: "rec",  to: "cand", label: "Schedule recruiter screen", baseHrs: 18, workHrs: 0.3, aiEligible: true  },
      { from: "rec",  to: "cand", label: "Conduct screen",            baseHrs: 4,  workHrs: 0.5, aiEligible: false },
      { from: "rec",  to: "i1",   label: "Schedule tech screen",      baseHrs: 28, workHrs: 0.3, aiEligible: true  },
      { from: "i1",   to: "cand", label: "Run tech screen",           baseHrs: 6,  workHrs: 1,   aiEligible: false },
      { from: "i1",   to: "hm",   label: "Tech screen feedback",      baseHrs: 18, workHrs: 0.5, aiEligible: false },
      { from: "rec",  to: "cand", label: "Schedule onsite (4 cals)",  baseHrs: 56, workHrs: 1.5, aiEligible: true  },
      { from: "i2",   to: "cand", label: "Onsite — system design",    baseHrs: 4,  workHrs: 1,   aiEligible: false },
      { from: "i3",   to: "cand", label: "Onsite — coding",           baseHrs: 4,  workHrs: 1,   aiEligible: false },
      { from: "i4",   to: "cand", label: "Onsite — values",           baseHrs: 4,  workHrs: 1,   aiEligible: false },
      { from: "i2",   to: "hm",   label: "Collect debrief feedback",  baseHrs: 36, workHrs: 1,   aiEligible: true  },
      { from: "hm",   to: "rec",  label: "Hire/no-hire decision",     baseHrs: 18, workHrs: 0.5, aiEligible: false },
      { from: "comp", to: "hm",   label: "Compensation approval",     baseHrs: 28, workHrs: 1,   aiEligible: true  },
      { from: "rec",  to: "cand", label: "Extend offer",              baseHrs: 12, workHrs: 0.5, aiEligible: true  },
      { from: "cand", to: "rec",  label: "Negotiate / accept",        baseHrs: 56, workHrs: 1.5, aiEligible: false },
    ],
  },

  // ============================================================
  // Enterprise RFP & Vendor Selection
  // ============================================================
  rfp: {
    id: "rfp",
    name: "Enterprise RFP & Vendor Selection",
    icon: "📋",
    category: "trade",
    short: "Draft RFP, evaluate 3 vendors, security & legal review, award.",
    desc: "Buyer team runs a structured selection across three vendors, with stakeholders from procurement, IT, security, legal, and finance — plus the vendors' own sales and SE teams.",
    pills: ["~13 handoffs", "5 orgs", "Typical: 6–12 weeks"],
    industries: ["all"],
    sizes: ["mid", "enterprise"],
    functions: ["procurement", "operations", "legal"],
    orgs: [
      { id: "buyer",    name: "Buyer org",  color: "#f59f4a" },
      { id: "vendora",  name: "Vendor A",   color: "#6dd6ff" },
      { id: "vendorb",  name: "Vendor B",   color: "#b39bff" },
      { id: "vendorc",  name: "Vendor C",   color: "#4ade80" },
      { id: "external", name: "References", color: "#aaaaaa" },
    ],
    parties: [
      { id: "bproc",   org: "buyer",    name: "Procurement Lead" },
      { id: "bdept",   org: "buyer",    name: "Department Owner" },
      { id: "bsec",    org: "buyer",    name: "Security / IT" },
      { id: "blegal",  org: "buyer",    name: "Legal" },
      { id: "bfin",    org: "buyer",    name: "Finance" },
      { id: "vAsales", org: "vendora",  name: "Vendor A — Sales" },
      { id: "vBsales", org: "vendorb",  name: "Vendor B — Sales" },
      { id: "vCsales", org: "vendorc",  name: "Vendor C — Sales" },
      { id: "ref",     org: "external", name: "Customer References" },
    ],
    steps: [
      { from: "bproc",   to: "bdept",   label: "Draft RFP w/ stakeholders",   baseHrs: 56, workHrs: 12,  aiEligible: true  },
      { from: "bproc",   to: "vAsales", label: "Issue RFP to 3 vendors",      baseHrs: 6,  workHrs: 0.5, aiEligible: true  },
      { from: "vAsales", to: "bproc",   label: "Vendor clarifications",       baseHrs: 56, workHrs: 4,   aiEligible: true  },
      { from: "vAsales", to: "bproc",   label: "Vendor A submits proposal",   baseHrs: 80, workHrs: 0.5, aiEligible: true  },
      { from: "vBsales", to: "bproc",   label: "Vendor B submits proposal",   baseHrs: 80, workHrs: 0.5, aiEligible: true  },
      { from: "vCsales", to: "bproc",   label: "Vendor C submits proposal",   baseHrs: 80, workHrs: 0.5, aiEligible: true  },
      { from: "bproc",   to: "bdept",   label: "Score & shortlist",           baseHrs: 36, workHrs: 8,   aiEligible: true  },
      { from: "bdept",   to: "vAsales", label: "Schedule finalist demos",     baseHrs: 36, workHrs: 1,   aiEligible: true  },
      { from: "ref",     to: "bdept",   label: "Reference calls",             baseHrs: 56, workHrs: 4,   aiEligible: true  },
      { from: "bsec",    to: "vAsales", label: "Security review of finalist", baseHrs: 60, workHrs: 12,  aiEligible: false },
      { from: "blegal",  to: "vAsales", label: "Legal review (MSA, DPA)",     baseHrs: 60, workHrs: 8,   aiEligible: false },
      { from: "bfin",    to: "bproc",   label: "Budget / spend approval",     baseHrs: 28, workHrs: 2,   aiEligible: true  },
      { from: "bproc",   to: "vAsales", label: "Negotiate & award",           baseHrs: 48, workHrs: 6,   aiEligible: false },
    ],
  },

  // ============================================================
  // Vendor Onboarding & Risk Review
  // ============================================================
  "vendor-onboarding": {
    id: "vendor-onboarding",
    name: "Vendor Onboarding & Risk Review",
    icon: "📦",
    category: "insurance",
    short: "Risk questionnaire, security review, MSA, ERP setup, sanctions.",
    desc: "Bringing a new vendor live across procurement, IT/security, legal, and finance — plus the vendor's own sales and account teams. Most of the calendar burns in security artifact ping-pong and contract redlining.",
    pills: ["~12 handoffs", "3 orgs", "Typical: 5–10 weeks"],
    industries: ["all", "financial-services", "healthcare", "manufacturing"],
    sizes: ["mid", "enterprise"],
    functions: ["procurement", "legal", "finance", "operations"],
    orgs: [
      { id: "buyer",  name: "Your org",        color: "#6dd6ff" },
      { id: "vendor", name: "New vendor",      color: "#f59f4a" },
      { id: "external", name: "External services", color: "#b39bff" },
    ],
    parties: [
      { id: "proc",    org: "buyer",    name: "Procurement Lead" },
      { id: "secrev",  org: "buyer",    name: "IT / Security" },
      { id: "blegal",  org: "buyer",    name: "Legal" },
      { id: "bfin",    org: "buyer",    name: "Finance / AP" },
      { id: "vsales",  org: "vendor",   name: "Vendor Sales" },
      { id: "vam",     org: "vendor",   name: "Vendor Account Mgmt" },
      { id: "vlegal",  org: "vendor",   name: "Vendor Legal" },
      { id: "kycprov", org: "external", name: "KYC / Sanctions" },
    ],
    steps: [
      { from: "proc",   to: "vsales",  label: "Send onboarding packet",        baseHrs: 6,  workHrs: 0.5, aiEligible: true  },
      { from: "vsales", to: "proc",    label: "Return risk questionnaire",     baseHrs: 36, workHrs: 4,   aiEligible: true  },
      { from: "proc",   to: "secrev",  label: "Route security review",         baseHrs: 8,  workHrs: 0.3, aiEligible: true  },
      { from: "secrev", to: "vsales",  label: "Request SOC 2, pen test",       baseHrs: 16, workHrs: 1,   aiEligible: true  },
      { from: "vsales", to: "secrev",  label: "Provide security artifacts",    baseHrs: 32, workHrs: 2,   aiEligible: true  },
      { from: "secrev", to: "proc",    label: "Security sign-off",             baseHrs: 36, workHrs: 4,   aiEligible: false },
      { from: "proc",   to: "blegal",  label: "Route MSA / DPA review",        baseHrs: 12, workHrs: 0.3, aiEligible: true  },
      { from: "blegal", to: "vlegal",  label: "Redline MSA",                   baseHrs: 60, workHrs: 6,   aiEligible: false },
      { from: "vlegal", to: "blegal",  label: "Counter-redlines",              baseHrs: 48, workHrs: 4,   aiEligible: false },
      { from: "blegal", to: "proc",    label: "Final legal sign-off",          baseHrs: 18, workHrs: 1,   aiEligible: false },
      { from: "proc",   to: "bfin",    label: "Set up in ERP & issue PO",      baseHrs: 24, workHrs: 1,   aiEligible: true  },
      { from: "proc",   to: "kycprov", label: "Run KYC / sanctions screening", baseHrs: 12, workHrs: 0.5, aiEligible: true  },
      { from: "bfin",   to: "vam",     label: "Go-live & invoice setup",       baseHrs: 16, workHrs: 0.5, aiEligible: true  },
    ],
  },

  // ============================================================
  // Sales Quote → Signed Contract
  // ============================================================
  "sales-quote": {
    id: "sales-quote",
    name: "Sales Quote → Signed Contract",
    icon: "✍️",
    category: "formation",
    short: "Quote, deal-desk approval, redlines, customer signoff.",
    desc: "From the AE sending a quote to ink on paper. Most of the elapsed time is the customer's procurement and legal review — but the seller's own deal-desk + legal cycle adds another week or two before the contract even leaves the building.",
    pills: ["~13 handoffs", "2 orgs", "Typical: 2–6 weeks"],
    industries: ["all", "saas", "professional"],
    sizes: ["smb", "mid", "enterprise"],
    functions: ["sales", "legal", "finance"],
    orgs: [
      { id: "vendor",   name: "Vendor (you)", color: "#6dd6ff" },
      { id: "customer", name: "Customer",     color: "#f59f4a" },
    ],
    parties: [
      { id: "ae",       org: "vendor",   name: "Account Executive" },
      { id: "dd",       org: "vendor",   name: "Deal Desk" },
      { id: "vfin",     org: "vendor",   name: "Finance / Discount" },
      { id: "vlegal",   org: "vendor",   name: "Vendor Legal" },
      { id: "buyer",    org: "customer", name: "Buyer / Champion" },
      { id: "cproc",    org: "customer", name: "Procurement" },
      { id: "clegal",   org: "customer", name: "Customer Legal" },
      { id: "cfin",     org: "customer", name: "Customer Finance" },
    ],
    steps: [
      { from: "ae",     to: "buyer",  label: "Send initial quote",         baseHrs: 4,  workHrs: 0.3, aiEligible: true  },
      { from: "buyer",  to: "ae",     label: "Request quote revisions",    baseHrs: 24, workHrs: 0.5, aiEligible: true  },
      { from: "ae",     to: "dd",     label: "Submit for deal-desk review",baseHrs: 6,  workHrs: 0.5, aiEligible: true  },
      { from: "dd",     to: "vfin",   label: "Margin / discount check",    baseHrs: 16, workHrs: 1,   aiEligible: true  },
      { from: "vfin",   to: "ae",     label: "Pricing approval",           baseHrs: 12, workHrs: 0.5, aiEligible: true  },
      { from: "ae",     to: "vlegal", label: "Request contract draft",     baseHrs: 8,  workHrs: 0.3, aiEligible: true  },
      { from: "vlegal", to: "buyer",  label: "Send contract draft",        baseHrs: 12, workHrs: 1,   aiEligible: true  },
      { from: "buyer",  to: "cproc",  label: "Route to procurement",       baseHrs: 16, workHrs: 0.3, aiEligible: true  },
      { from: "cproc",  to: "clegal", label: "Procurement → Legal review", baseHrs: 28, workHrs: 1,   aiEligible: true  },
      { from: "clegal", to: "vlegal", label: "Customer redlines",          baseHrs: 56, workHrs: 6,   aiEligible: false },
      { from: "vlegal", to: "clegal", label: "Vendor counter-redlines",    baseHrs: 48, workHrs: 4,   aiEligible: false },
      { from: "cfin",   to: "ae",     label: "Customer finance approval",  baseHrs: 24, workHrs: 1,   aiEligible: true  },
      { from: "ae",     to: "buyer",  label: "Send for signature",         baseHrs: 12, workHrs: 0.3, aiEligible: true  },
    ],
  },

  // ============================================================
  // Expense Exception Approval
  // ============================================================
  "expense-exception": {
    id: "expense-exception",
    name: "Expense Exception Approval",
    icon: "💳",
    category: "taxation",
    short: "Out-of-policy expense routed through finance, manager, CFO.",
    desc: "An employee submits an expense outside policy (over-limit dinner, unapproved vendor, missing receipt). It bounces between manager, department head, finance reviewer, and — for the bigger ones — the CFO. Easy money but the cycle drags weeks.",
    pills: ["~9 handoffs", "2 orgs", "Typical: 1–3 weeks"],
    industries: ["all"],
    sizes: ["smb", "mid", "enterprise"],
    functions: ["finance", "operations"],
    orgs: [
      { id: "emp",  name: "Employee",       color: "#f59f4a" },
      { id: "fin",  name: "Finance org",    color: "#6dd6ff" },
    ],
    parties: [
      { id: "submitter", org: "emp", name: "Employee" },
      { id: "manager",   org: "fin", name: "Direct Manager" },
      { id: "depthead",  org: "fin", name: "Dept Head" },
      { id: "finrev",    org: "fin", name: "Finance Reviewer" },
      { id: "ap",        org: "fin", name: "Accounts Payable" },
      { id: "cfo",       org: "fin", name: "CFO Approver" },
    ],
    steps: [
      { from: "submitter", to: "manager",  label: "Submit out-of-policy expense", baseHrs: 4,  workHrs: 0.3, aiEligible: true  },
      { from: "manager",   to: "submitter",label: "Manager request for context",  baseHrs: 18, workHrs: 0.3, aiEligible: true  },
      { from: "submitter", to: "manager",  label: "Provide receipts / context",   baseHrs: 14, workHrs: 0.5, aiEligible: true  },
      { from: "manager",   to: "depthead", label: "Manager approval → dept head", baseHrs: 22, workHrs: 0.3, aiEligible: true  },
      { from: "depthead",  to: "finrev",   label: "Dept head approval",           baseHrs: 28, workHrs: 0.3, aiEligible: true  },
      { from: "finrev",    to: "manager",  label: "Policy bounce / questions",    baseHrs: 24, workHrs: 1,   aiEligible: true  },
      { from: "manager",   to: "finrev",   label: "Resolve policy bounce",        baseHrs: 18, workHrs: 0.5, aiEligible: true  },
      { from: "finrev",    to: "cfo",      label: "Escalate to CFO (over $5k)",   baseHrs: 36, workHrs: 0.5, aiEligible: true  },
      { from: "cfo",       to: "ap",       label: "CFO sign-off, route to AP",    baseHrs: 24, workHrs: 0.5, aiEligible: true  },
      { from: "ap",        to: "submitter",label: "Reimburse",                    baseHrs: 16, workHrs: 0.3, aiEligible: true  },
    ],
  },

  // ============================================================
  // Software Access Request (new tool / license)
  // ============================================================
  "software-access": {
    id: "software-access",
    name: "Software Access Request",
    icon: "🔑",
    category: "data",
    short: "Employee requests a new tool; IT, security, finance signoff.",
    desc: "Someone needs a tool to do their job. Their request bounces through manager approval, IT provisioning, security review, finance budget check, and finally the vendor for license assignment. Trivially small, dragged for weeks.",
    pills: ["~10 handoffs", "2 orgs", "Typical: 1–4 weeks"],
    industries: ["all", "saas", "financial-services"],
    sizes: ["mid", "enterprise"],
    functions: ["it", "operations"],
    orgs: [
      { id: "internal", name: "Your org",  color: "#6dd6ff" },
      { id: "vendor",   name: "SaaS vendor", color: "#f59f4a" },
    ],
    parties: [
      { id: "emp",     org: "internal", name: "Requesting Employee" },
      { id: "mgr",     org: "internal", name: "Direct Manager" },
      { id: "it",      org: "internal", name: "IT Admin" },
      { id: "sec",     org: "internal", name: "Security Review" },
      { id: "fin",     org: "internal", name: "Finance / Budget" },
      { id: "sso",     org: "internal", name: "Identity / SSO" },
      { id: "vsales",  org: "vendor",   name: "Vendor Sales / Success" },
    ],
    steps: [
      { from: "emp",    to: "mgr",    label: "Submit request",                baseHrs: 4,  workHrs: 0.2, aiEligible: true  },
      { from: "mgr",    to: "emp",    label: "Ask for justification",         baseHrs: 18, workHrs: 0.3, aiEligible: true  },
      { from: "emp",    to: "mgr",    label: "Provide justification",         baseHrs: 12, workHrs: 0.3, aiEligible: true  },
      { from: "mgr",    to: "it",     label: "Manager approves → IT",         baseHrs: 14, workHrs: 0.2, aiEligible: true  },
      { from: "it",     to: "sec",    label: "Route security review",         baseHrs: 16, workHrs: 0.3, aiEligible: true  },
      { from: "sec",    to: "it",     label: "Security signoff",              baseHrs: 28, workHrs: 1.5, aiEligible: false },
      { from: "it",     to: "fin",    label: "Budget / spend check",          baseHrs: 18, workHrs: 0.5, aiEligible: true  },
      { from: "fin",    to: "vsales", label: "Procure seat / negotiate price",baseHrs: 28, workHrs: 1,   aiEligible: true  },
      { from: "vsales", to: "sso",    label: "Vendor → SSO provisioning",     baseHrs: 14, workHrs: 0.5, aiEligible: true  },
      { from: "sso",    to: "emp",    label: "Grant access, notify",          baseHrs: 8,  workHrs: 0.3, aiEligible: true  },
    ],
  },

  // ============================================================
  // Loan Origination & Underwriting (financial-services)
  // ============================================================
  "loan-origination": {
    id: "loan-origination",
    name: "Loan Origination & Underwriting",
    icon: "🏦",
    category: "certification",
    short: "Application → credit, compliance, legal, funding.",
    desc: "A commercial loan from application through funding. Front office takes the deal; credit, compliance, and legal each pass it through their pipelines; an external appraiser weighs in; and the borrower keeps getting asked for one more document.",
    pills: ["~13 handoffs", "3 orgs", "Typical: 3–8 weeks"],
    industries: ["financial-services"],
    sizes: ["mid", "enterprise"],
    functions: ["sales", "legal", "operations"],
    orgs: [
      { id: "bank",     name: "Bank",       color: "#6dd6ff" },
      { id: "borrower", name: "Borrower",   color: "#f59f4a" },
      { id: "external", name: "Appraiser / external", color: "#b39bff" },
    ],
    parties: [
      { id: "rm",        org: "bank",     name: "Relationship Mgr" },
      { id: "uw",        org: "bank",     name: "Credit / Underwriting" },
      { id: "comp",      org: "bank",     name: "Compliance / BSA" },
      { id: "blegal",    org: "bank",     name: "Bank Legal" },
      { id: "closer",    org: "bank",     name: "Loan Closer" },
      { id: "applicant", org: "borrower", name: "Applicant" },
      { id: "blegalc",   org: "borrower", name: "Borrower Counsel" },
      { id: "appraiser", org: "external", name: "Appraiser" },
    ],
    steps: [
      { from: "applicant", to: "rm",         label: "Submit application",         baseHrs: 8,  workHrs: 0.5, aiEligible: true  },
      { from: "rm",        to: "applicant",  label: "Request supporting docs",    baseHrs: 18, workHrs: 0.5, aiEligible: true  },
      { from: "applicant", to: "rm",         label: "Provide financials, K-1s",   baseHrs: 56, workHrs: 4,   aiEligible: true  },
      { from: "rm",        to: "uw",         label: "Route to credit",            baseHrs: 12, workHrs: 0.3, aiEligible: true  },
      { from: "uw",        to: "applicant",  label: "Credit Q&A — missing items", baseHrs: 28, workHrs: 1,   aiEligible: true  },
      { from: "applicant", to: "uw",         label: "Resolve credit Q&A",         baseHrs: 36, workHrs: 2,   aiEligible: true  },
      { from: "rm",        to: "appraiser",  label: "Order appraisal",            baseHrs: 18, workHrs: 0.5, aiEligible: true  },
      { from: "appraiser", to: "uw",         label: "Appraisal returned",         baseHrs: 80, workHrs: 0.5, aiEligible: true  },
      { from: "uw",        to: "comp",       label: "Compliance / BSA review",    baseHrs: 36, workHrs: 4,   aiEligible: false },
      { from: "comp",      to: "uw",         label: "Compliance sign-off",        baseHrs: 24, workHrs: 2,   aiEligible: false },
      { from: "blegal",    to: "blegalc",    label: "Loan docs → borrower legal", baseHrs: 36, workHrs: 4,   aiEligible: false },
      { from: "blegalc",   to: "blegal",     label: "Borrower redlines",          baseHrs: 56, workHrs: 6,   aiEligible: false },
      { from: "closer",    to: "applicant",  label: "Close & fund",               baseHrs: 24, workHrs: 2,   aiEligible: true  },
    ],
  },

  // ============================================================
  // Marketing Campaign Approval
  // ============================================================
  "marketing-campaign": {
    id: "marketing-campaign",
    name: "Marketing Campaign Approval",
    icon: "📣",
    category: "ip",
    short: "Creative review, brand, legal, exec signoff, vendor production.",
    desc: "A campaign from brief to in-market. Creative iterates with the brand team. Legal sweeps it for claims. Comms signs off on messaging. An exec reviews the final cut. An external vendor (agency or production) ships the assets.",
    pills: ["~10 handoffs", "2 orgs", "Typical: 3–6 weeks"],
    industries: ["all", "saas", "retail", "media"],
    sizes: ["mid", "enterprise"],
    functions: ["marketing", "legal"],
    orgs: [
      { id: "mktg",   name: "Your org",        color: "#6dd6ff" },
      { id: "agency", name: "Agency / vendor", color: "#f59f4a" },
    ],
    parties: [
      { id: "pm",     org: "mktg",   name: "Campaign Manager" },
      { id: "creative", org: "mktg", name: "Creative / Design" },
      { id: "brand",  org: "mktg",   name: "Brand Reviewer" },
      { id: "mlegal", org: "mktg",   name: "Marketing Legal" },
      { id: "comms",  org: "mktg",   name: "Communications" },
      { id: "exec",   org: "mktg",   name: "VP Marketing" },
      { id: "agp",    org: "agency", name: "Agency Producer" },
    ],
    steps: [
      { from: "pm",       to: "creative", label: "Brief & kickoff",            baseHrs: 8,  workHrs: 2,   aiEligible: true  },
      { from: "creative", to: "pm",       label: "V1 creative",                baseHrs: 36, workHrs: 8,   aiEligible: false },
      { from: "pm",       to: "brand",    label: "Brand review",               baseHrs: 14, workHrs: 0.5, aiEligible: true  },
      { from: "brand",    to: "creative", label: "Brand feedback / revisions", baseHrs: 24, workHrs: 1,   aiEligible: false },
      { from: "creative", to: "pm",       label: "V2 creative",                baseHrs: 28, workHrs: 4,   aiEligible: false },
      { from: "pm",       to: "mlegal",   label: "Legal review for claims",    baseHrs: 24, workHrs: 0.5, aiEligible: true  },
      { from: "mlegal",   to: "pm",       label: "Legal redlines",             baseHrs: 28, workHrs: 2,   aiEligible: false },
      { from: "pm",       to: "comms",    label: "Comms / messaging review",   baseHrs: 18, workHrs: 0.5, aiEligible: true  },
      { from: "comms",    to: "exec",     label: "VP final approval",          baseHrs: 28, workHrs: 0.5, aiEligible: true  },
      { from: "exec",     to: "agp",      label: "Hand off to agency to ship", baseHrs: 12, workHrs: 0.5, aiEligible: true  },
    ],
  },

  // ============================================================
  // Patient Referral & Prior Authorization (healthcare)
  // ============================================================
  "patient-referral": {
    id: "patient-referral",
    name: "Patient Referral & Prior Auth",
    icon: "🏥",
    category: "industry",
    short: "Referral routed through insurer for prior authorization.",
    desc: "PCP refers a patient to a specialist. The insurer has to pre-authorize the procedure before the patient is scheduled. Records, codes, and clinical justifications bounce between three offices while the patient waits.",
    pills: ["~10 handoffs", "3 orgs", "Typical: 1–3 weeks"],
    industries: ["healthcare"],
    sizes: ["smb", "mid", "enterprise"],
    functions: ["operations", "legal"],
    orgs: [
      { id: "clinic",  name: "PCP clinic",  color: "#6dd6ff" },
      { id: "insurer", name: "Insurer",     color: "#f59f4a" },
      { id: "spec",    name: "Specialist",  color: "#b39bff" },
    ],
    parties: [
      { id: "pcp",       org: "clinic",  name: "Primary Care Physician" },
      { id: "intake",    org: "clinic",  name: "Referral Coordinator" },
      { id: "patient",   org: "clinic",  name: "Patient" },
      { id: "insrev",    org: "insurer", name: "Insurer Reviewer" },
      { id: "insmed",    org: "insurer", name: "Insurer Medical Director" },
      { id: "specsched", org: "spec",    name: "Specialist Office" },
      { id: "specdoc",   org: "spec",    name: "Specialist Physician" },
    ],
    steps: [
      { from: "pcp",       to: "intake",    label: "Order referral",              baseHrs: 6,  workHrs: 0.3, aiEligible: true  },
      { from: "intake",    to: "insrev",    label: "Submit prior auth request",   baseHrs: 12, workHrs: 0.5, aiEligible: true  },
      { from: "insrev",    to: "intake",    label: "Request additional records",  baseHrs: 36, workHrs: 1,   aiEligible: true  },
      { from: "intake",    to: "pcp",       label: "Pull additional records",     baseHrs: 18, workHrs: 1,   aiEligible: true  },
      { from: "pcp",       to: "intake",    label: "Provide clinical justification", baseHrs: 24, workHrs: 1.5, aiEligible: false },
      { from: "intake",    to: "insrev",    label: "Resubmit with records",       baseHrs: 18, workHrs: 0.5, aiEligible: true  },
      { from: "insrev",    to: "insmed",    label: "Medical director review",     baseHrs: 36, workHrs: 1,   aiEligible: false },
      { from: "insmed",    to: "intake",    label: "Auth approved",               baseHrs: 28, workHrs: 0.5, aiEligible: true  },
      { from: "intake",    to: "specsched", label: "Schedule with specialist",    baseHrs: 18, workHrs: 0.5, aiEligible: true  },
      { from: "specsched", to: "patient",   label: "Patient appointment booked",  baseHrs: 14, workHrs: 0.3, aiEligible: true  },
    ],
  },
};

// Pre-baked palette of org colors a custom-generated process can pick from.
// (Re-exported alias kept for backwards compat with older imports.)
